import { useSyncExternalStore } from 'react'
import { sdk } from '@/lib/sdk'
import type { DownloadFileLimits, DownloadItem } from '@/types/downloads'
import { StorageRefused, UploadCanceled, type UploadProgress, uploadItemFile } from './downloadUpload'
import { apiMessage, checkFile } from './downloadUtils'

/**
 * Bulk upload for a Downloads space (F-75): every file becomes its own item, titled after the file.
 * The queue lives at module level (like the gallery's), so the dialog can be closed and the files
 * keep going. Each file is independent, so one failure never stops the others:
 *
 *   waiting → creating (the item, a draft) → sending % (upload-url, PUT, confirm)
 *           → publishing (only when asked) → done | failed (reason + retry from the step that failed)
 *
 * Only the existing endpoints are used, one item and one upload per file.
 */

export type BulkStatus = 'waiting' | 'creating' | 'sending' | 'publishing' | 'done' | 'failed'

/** Refused before sending (`tooBig`, `type`, `empty`), the storage refused (`storage`) or the API said no (`api`). */
export type BulkError = { kind: 'tooBig' | 'type' | 'empty' | 'storage' | 'api'; message?: string | null }

export interface BulkEntry {
  id: string
  spaceId: string
  file: File
  title: string
  categoryId: string | null
  publish: boolean
  status: BulkStatus
  progress: UploadProgress
  /** The item exists; a retry does not create it again. */
  itemId?: string
  /** The file is on the item; a retry only publishes. */
  uploaded?: boolean
  error?: BulkError
}

interface State {
  entries: BulkEntry[]
}

const CONCURRENCY = 2
const TITLE_MAX = 140

let state: State = { entries: [] }
const listeners = new Set<() => void>()
const savedListeners = new Set<(spaceId: string, item: DownloadItem) => void>()
const controllers = new Map<string, AbortController>()
let seq = 0

const emit = () => listeners.forEach((listener) => listener())

const setEntries = (entries: BulkEntry[]) => {
  state = { entries }
  syncLeaveGuard()
  emit()
}

const find = (id: string) => state.entries.find((entry) => entry.id === id)

const patch = (id: string, changes: Partial<BulkEntry>) => {
  if (!find(id)) return
  setEntries(state.entries.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)))
}

const isActive = (entry: BulkEntry) => entry.status !== 'done' && entry.status !== 'failed'

const onBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault()
  event.returnValue = ''
}
let guarding = false
const syncLeaveGuard = () => {
  const busy = state.entries.some(isActive)
  if (busy === guarding || typeof window === 'undefined') return
  guarding = busy
  if (busy) window.addEventListener('beforeunload', onBeforeUnload)
  else window.removeEventListener('beforeunload', onBeforeUnload)
}

/** The file name without its extension, as the item's starting title. */
export const titleFromFileName = (name: string): string => {
  const dot = name.lastIndexOf('.')
  const base = (dot > 0 ? name.slice(0, dot) : name).replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim()
  return (base || name).slice(0, TITLE_MAX)
}

const notifySaved = (spaceId: string, item: DownloadItem) => savedListeners.forEach((listener) => listener(spaceId, item))

/** One file, from the step it stopped at to the end. Never throws: the entry keeps its error. */
const run = async (id: string) => {
  const start = find(id)
  if (!start) return
  const controller = new AbortController()
  controllers.set(id, controller)
  let itemId = start.itemId
  try {
    if (!itemId) {
      patch(id, { status: 'creating', progress: { percent: 0 }, error: undefined })
      /* A batch never fires one e-mail notice per file (F-104): every item goes out with the switch off. */
      const created = await sdk.downloads.createItem({ spaceId: start.spaceId, title: start.title, categoryId: start.categoryId, notifyByEmail: false })
      itemId = created.id
      patch(id, { itemId })
      if (controller.signal.aborted) throw new UploadCanceled()
    }
    let item: DownloadItem | null = null
    if (!find(id)?.uploaded) {
      patch(id, { status: 'sending', progress: { percent: 0 }, error: undefined })
      await uploadItemFile(itemId, start.file, (progress) => patch(id, { progress }), controller.signal)
      patch(id, { uploaded: true, progress: { percent: 100 } })
    }
    if (start.publish) {
      patch(id, { status: 'publishing', error: undefined })
      item = await sdk.downloads.updateItem(itemId, { status: 'published', notifyByEmail: false })
    } else {
      item = await sdk.downloads.getItem(itemId)
    }
    patch(id, { status: 'done' })
    notifySaved(start.spaceId, item)
  } catch (err) {
    if (err instanceof UploadCanceled) {
      await discard(id)
      return
    }
    const status = (err as { response?: { status?: number } })?.response?.status
    const error: BulkError = err instanceof StorageRefused
      ? { kind: 'storage' }
      : { kind: 'api', message: status ? apiMessage(err, '') : null }
    patch(id, { status: 'failed', error })
  } finally {
    controllers.delete(id)
  }
}

let pumping = false
/** Starts waiting entries while fewer than CONCURRENCY are running. */
const pump = () => {
  if (pumping) return
  pumping = true
  try {
    const running = state.entries.filter((e) => e.status === 'creating' || e.status === 'sending' || e.status === 'publishing').length
    const free = Math.max(0, CONCURRENCY - running)
    const next = state.entries.filter((e) => e.status === 'waiting').slice(0, free)
    for (const entry of next) {
      /* Marked before the await so a second pump does not take it again. */
      patch(entry.id, { status: 'creating' })
      void run(entry.id).finally(pump)
    }
  } finally {
    pumping = false
  }
}

/**
 * An item this queue created that never got its file is removed with its row, so a canceled or
 * dismissed file leaves no empty draft behind. An item that has its file stays (it is real content).
 */
const discard = async (id: string) => {
  const entry = find(id)
  setEntries(state.entries.filter((e) => e.id !== id))
  if (entry?.itemId && !entry.uploaded) {
    try { await sdk.downloads.deleteItem(entry.itemId) } catch { /* the empty draft stays; the team can delete it */ }
  }
}

export interface BulkOptions {
  spaceId: string
  categoryId: string | null
  publish: boolean
  /** The space's `fileLimits` (the known defaults when absent). */
  fileLimits?: DownloadFileLimits | null
}

/**
 * Adds the files to the queue: the ones the API would refuse fail at once, the rest go. Returns the
 * new rows' ids, so the caller can follow one (the "new download" opens its item when it lands).
 */
export const enqueueDownloadFiles = (files: File[], options: BulkOptions): string[] => {
  const added: BulkEntry[] = files.map((file) => {
    const problem = checkFile(file, options.fileLimits)
    return {
      id: `b${++seq}`,
      spaceId: options.spaceId,
      file,
      title: titleFromFileName(file.name),
      categoryId: options.categoryId,
      publish: options.publish,
      status: problem ? 'failed' : 'waiting',
      progress: { percent: 0 },
      ...(problem ? { error: { kind: problem } } : {})
    }
  })
  setEntries([...state.entries, ...added])
  pump()
  return added.map((entry) => entry.id)
}

/** Takes a finished row off the list (its item stays: it was opened). */
export const dismissDownloadEntry = (id: string) => {
  const entry = find(id)
  if (!entry || entry.status !== 'done') return
  setEntries(state.entries.filter((e) => e.id !== id))
}

/** Tries again from the step that failed. Files refused before sending cannot be retried. */
export const retryDownloadFile = (id: string) => {
  const entry = find(id)
  if (!entry || entry.status !== 'failed' || !canRetry(entry)) return
  patch(id, { status: 'waiting', error: undefined })
  pump()
}

export const canRetry = (entry: BulkEntry) => entry.status === 'failed' && (entry.error?.kind === 'api' || entry.error?.kind === 'storage')

/** Cancels a file on its way, or dismisses one that ended. */
export const removeDownloadFile = (id: string) => {
  const controller = controllers.get(id)
  if (controller) { controller.abort(); return }
  void discard(id)
}

/** Clears the finished rows of a space once nothing there is in flight. */
export const clearDownloadUploads = (spaceId: string) => {
  const mine = state.entries.filter((e) => e.spaceId === spaceId)
  if (mine.some(isActive)) return
  mine.filter((e) => e.status === 'failed').forEach((e) => { void discard(e.id) })
  setEntries(state.entries.filter((e) => e.spaceId !== spaceId))
}

/** Each item as soon as it is saved (and published, when asked), so the list grows file by file. */
export const onDownloadItemUploaded = (listener: (spaceId: string, item: DownloadItem) => void) => {
  savedListeners.add(listener)
  return () => { savedListeners.delete(listener) }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export interface BulkSummary {
  total: number
  done: number
  failed: number
  active: number
  finished: boolean
}

export const summarize = (entries: BulkEntry[]): BulkSummary => {
  const done = entries.filter((e) => e.status === 'done').length
  const failed = entries.filter((e) => e.status === 'failed').length
  const active = entries.length - done - failed
  return { total: entries.length, done, failed, active, finished: entries.length > 0 && active === 0 }
}

const EMPTY: BulkEntry[] = []
let lastAll: BulkEntry[] = EMPTY
const cache = new Map<string, BulkEntry[]>()

/** The queue of one space (rows keep their order). */
export const useDownloadUploads = (spaceId: string | undefined): BulkEntry[] =>
  useSyncExternalStore(subscribe, () => {
    if (state.entries !== lastAll) { lastAll = state.entries; cache.clear() }
    if (!spaceId) return EMPTY
    let mine = cache.get(spaceId)
    if (!mine) { mine = state.entries.filter((e) => e.spaceId === spaceId); cache.set(spaceId, mine) }
    return mine
  })
